// ███ THE COMMS FEED'S ORDER ███
//
// T0 (2026-10-26, brief-comms-media-and-day-notes.md). Until this existed the
// feed had NO chronological order at all: `getCollection` read `posts` with no
// `orderBy`, and the only sort was pinned-first followed by `return 0` for every
// other pair, so what a venue saw was Firestore's document-id order over
// `nanoid()` ids — stable, arbitrary, and indistinguishable from a bug.
//
// ⚠ `createdAt` IS NOT THE ORDER AND MUST NEVER BECOME IT. It is a locale string
// built in the poster's browser — measured below — with the YEAR LAST, so it
// cannot be compared lexicographically, and it carries no timezone, so it cannot
// be compared as an instant without inventing one. `postedAt` is a server
// timestamp written by `addPost`, and it is the only field in this module's
// contract. `createdAt` keeps being written because other code renders it.
//
// ⚠ A POST WITH NO `postedAt` SORTS LAST, NOT FIRST. Legacy posts predate the
// field. Sorting an absent value as 0 would put every historical post at the
// bottom, which is right, but reading it as `Number(undefined)` is NaN and NaN
// comparisons are all false — which makes a comparator non-transitive and the
// sort's output arbitrary again, in a way that looks fine on small arrays. The
// absent case is therefore decided BEFORE any arithmetic.
//
// ⚠ AND THE QUERY, NOT THIS COMPARATOR, DECIDES WHETHER SUCH A POST IS SEEN AT
// ALL. Firestore omits documents that do not carry the field an `orderBy` names,
// so once the feed query orders by `postedAt` a legacy post is not last — it is
// absent. That is what makes `scripts/backfill-post-posted-at.mjs` a
// prerequisite for the client rather than a tidy-up after it, and it is proved
// against a real emulator in `functions/live/missingFieldQueryExclusion.test.js`'s
// orderBy block rather than asserted from the documentation. This comparator still handles the
// absent case because it also runs over arrays assembled by "load older", and
// because a comparator that is only correct for the inputs somebody predicted is
// the defect this whole file exists to undo.

// Milliseconds for a post's `postedAt`, or null — never a coercion of a blank.
//
// The shapes this really receives, all four measured against the client SDK's
// behaviour rather than assumed:
//
//   * a `Timestamp` from a server read              -> has `toMillis()`
//   * `{ seconds, nanoseconds }` from a plain object (a test fixture, or a
//     document that has been through `JSON.parse(JSON.stringify(...))`)
//   * a `Date`, if a caller ever hands one in
//   * `null`, for a locally-added document whose `serverTimestamp()` has not yet
//     round-tripped — `onSnapshot` reports the field as null while
//     `hasPendingWrites` is true. Posts are created by a Cloud Function, so this
//     does not arise today, and it costs one line to be right about.
//
// Everything else — `''`, `[]`, `true`, `{}`, a string — is REFUSED rather than
// coerced. `Number('')` and `Number([])` are both 0, which would date a post to
// 1 January 1970 and sort it last for the wrong reason; naming the blanks is not
// enough, so the TYPE is allow-listed first.
export function postedAtMillis(post) {
  return timestampMillis(post && typeof post === 'object' ? post.postedAt : undefined);
}

// The same four shapes, for a timestamp field that is NOT `postedAt`.
//
// Extracted when T3 needed `lastReadAt` off a read marker. It is the same
// conversion and the same refusals; a second copy of it is how
// `memory/ticket-ddmmyyyy-parser-proliferation.md` reached seven implementations of one
// date parser, and this file's own header is an argument against exactly that.
// `postedAtMillis` keeps its name and its field because every existing caller
// passes a post, not a value.
export function timestampMillis(value) {
  if (value === null || value === undefined) return null;

  if (typeof value.toMillis === 'function') {
    const ms = value.toMillis();
    return Number.isFinite(ms) ? ms : null;
  }
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
    const ms = value.seconds * 1000 + Math.floor(nanos / 1e6);
    return Number.isFinite(ms) ? ms : null;
  }
  // A raw millisecond number is accepted LAST and only when finite, so a
  // `Timestamp`-shaped object can never fall through to it.
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  return null;
}

// Pinned first, then newest `postedAt` first, then anything with no `postedAt`,
// then by id so the result does not depend on the order the array arrived in.
//
// ⚠ THE ID TIEBREAK IS NOT DECORATION. `return 0` is what the old sort did for
// every pair, and a sort that returns 0 for everything delegates the whole
// question to the input order — which is exactly how the feed came to be ordered
// by `nanoid()`. Two posts can share a `postedAt` (a batch, or two posts inside
// one millisecond), and when they do the answer must still be the same on every
// render, or the feed reshuffles under the reader.
export function comparePosts(a, b) {
  const pinnedA = (a && a.pinned) === true;
  const pinnedB = (b && b.pinned) === true;
  if (pinnedA !== pinnedB) return pinnedA ? -1 : 1;

  const msA = postedAtMillis(a);
  const msB = postedAtMillis(b);
  if (msA === null && msB !== null) return 1;
  if (msB === null && msA !== null) return -1;
  if (msA !== null && msB !== null && msA !== msB) return msB - msA;

  const idA = a && typeof a.id === 'string' ? a.id : '';
  const idB = b && typeof b.id === 'string' ? b.id : '';
  if (idA === idB) return 0;
  return idA < idB ? -1 : 1;
}

// ⚠ THERE IS DELIBERATELY NO `orderFeed(posts)` HELPER HERE. One was written
// and removed in review: its only caller was its own test file. `Comms.vue`
// sorts the array its own `.filter()` just produced, so a wrapper that copies
// first would protect nothing, and an exported function whose only caller is a
// test reads as a working part of the system while being reachable from nowhere
// — the shape recorded in CLAUDE.md as "the writer exists; it has no caller".
// Callers sort their own copy and pass `comparePosts`.

// The window the feed listener asks for, and what "load older" grows it to.
//
// One constant for both, so the control and the query cannot disagree about the
// number — the measured defect of a count computed from one array and described
// by a sentence derived from another.
export const FEED_PAGE_SIZE = 100;
export const nextFeedWindow = (current) => {
  const n = typeof current === 'number' && Number.isFinite(current) && current > 0
    ? Math.floor(current)
    : FEED_PAGE_SIZE;
  return n + FEED_PAGE_SIZE;
};
